<template>
  <div
    class="comment-item"
    :style="{
      paddingLeft: depth > 0 ? `${Math.min(depth, 5) * 20}px` : '0',
      borderLeft: depth > 0 ? '2px solid rgb(40, 46, 53)' : 'none'
    }"
  >
    <!-- Comment Card -->
    <div class="comment-card">
      <FeedItem
        :submission="comment"
        :showFooter="true"
        :clickable="false"
        :showAsCard="false"
        @comment-click="handleReplyClick"
        @attachment-click="handleAttachmentClick"
        @data-click="handleDataClick"
      />
    </div>

    <!-- Show Comments Link (only when collapsed) -->
    <div v-if="comment.comment_count > 0 && !expanded" class="show-comments-link">
      <button
        class="expand-link"
        @click="toggleNestedComments"
        :disabled="loadingNested"
      >
        <font-awesome-icon
          v-if="loadingNested"
          :icon="['fas', 'spinner']"
          spin
        />
        <span v-else>
          Show {{ comment.comment_count }}
          {{ comment.comment_count === 1 ? 'comment' : 'comments' }}
        </span>
      </button>
    </div>

    <!-- Nested Comments Section -->
    <div v-if="expanded && nestedComments.length > 0" class="nested-comments">
      <!-- Collapse button on left border -->
      <button
        class="collapse-button"
        @click="toggleNestedComments"
        title="Hide comments"
      >
        <font-awesome-icon :icon="['fas', 'minus']" />
      </button>
      <CommentItem
        v-for="nestedComment in nestedComments"
        :key="nestedComment.id"
        :comment="nestedComment"
        :depth="depth + 1"
      />

      <!-- Load More Button -->
      <button
        v-if="hasMore"
        class="load-more-button"
        @click="loadMoreComments"
        :disabled="loadingMore"
      >
        <font-awesome-icon
          v-if="loadingMore"
          :icon="['fas', 'spinner']"
          spin
        />
        <span v-else>Load more comments</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import Submission from '@/api/Submission.js'
import SubmissionComposer from '@/components/SubmissionComposer.vue'
import FeedItem from '@/components/Feed/FeedItem.vue'
import SubmissionDetail from './SubmissionDetail.vue'
import { useMainStore } from '@/stores/main'

const props = defineProps({
  comment: {
    type: Object,
    required: true
  },
  depth: {
    type: Number,
    default: 0
  }
})

const mainStore = useMainStore()

// Local state
const expanded = ref(false)
const nestedComments = ref([])
const loadingNested = ref(false)
const loadingMore = ref(false)
const offset = ref(0)
const hasMore = ref(false)
const limit = 20

// Computed properties
const userName = computed(() => {
  return props.comment.user?.name || 'Anonymous'
})

// Methods
function handleReplyClick() {
  mainStore.show_modal(SubmissionComposer, {
    parentSubmission: props.comment,
    placeholder: `Reply to ${userName.value}...`,
    maxChars: 2000
  })
}

function handleAttachmentClick() {
  // Navigate to parent submission's attachments tab
  mainStore.show_slideover(
    SubmissionDetail,
    {
      submissionId: props.comment.parent_id,
      activeTab: 'attachments'
    },
    { width: 50 },
    'Attachments'
  )
}

function handleDataClick() {
  // Navigate to parent submission's data tab
  mainStore.show_slideover(
    SubmissionDetail,
    {
      submissionId: props.comment.parent_id,
      activeTab: 'data'
    },
    { width: 50 },
    'Data'
  )
}

async function toggleNestedComments() {
  if (expanded.value) {
    // Collapse
    expanded.value = false
  } else {
    // Expand and fetch nested comments if not already loaded
    if (nestedComments.value.length === 0) {
      await fetchNestedComments()
    }
    expanded.value = true
  }
}

async function fetchNestedComments() {
  loadingNested.value = true
  try {
    const response = await Submission.getSubmissionComments(
      props.comment.id,
      offset.value,
      limit
    )

    if (response.status === 'success') {
      const responseData = response.data || {}
      nestedComments.value = responseData.comments || []
      hasMore.value = responseData.has_more || false
      offset.value = limit
    }
  } catch (error) {
    console.error('Failed to fetch nested comments:', error)
  } finally {
    loadingNested.value = false
  }
}

async function loadMoreComments() {
  loadingMore.value = true
  try {
    const response = await Submission.getSubmissionComments(
      props.comment.id,
      offset.value,
      limit
    )

    if (response.status === 'success') {
      const responseData = response.data || {}
      const newComments = responseData.comments || []
      nestedComments.value.push(...newComments)
      hasMore.value = responseData.has_more || false
      offset.value += limit
    }
  } catch (error) {
    console.error('Failed to load more comments:', error)
  } finally {
    loadingMore.value = false
  }
}

async function handleRefresh(data) {
  // If this is a reply to our comment, refresh nested comments if expanded
  if (expanded.value) {
    offset.value = 0
    await fetchNestedComments()
  }
}

// Lifecycle
onMounted(() => {
  mainStore.event_on('submission-created', handleRefresh)
})

onBeforeUnmount(() => {
  // Event emitter cleanup is handled by the store
  // No explicit cleanup needed as EventEmitter manages listeners
})
</script>

<style scoped>
.comment-item {
  margin-bottom: 0.75rem;
  transition: all 0.2s ease;
}

.comment-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0.75rem;
  padding: 1rem;
  transition: background 0.2s;
  margin-bottom: 0.5rem;
}

.comment-card:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* Show Comments Link */
.show-comments-link {
  padding: 0.5rem 1rem;
  margin-bottom: 0.5rem;
}

.expand-link {
  background: none;
  border: none;
  color: #6B7280;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.expand-link:hover:not(:disabled) {
  color: #a855f7;
}

.expand-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.expand-link svg {
  width: 0.875rem;
  height: 0.875rem;
}

/* Nested Comments */
.nested-comments {
  margin-top: 0.75rem;
  margin-left: 0.75rem;
  animation: slideDown 0.3s ease;
  position: relative;
}

/* Collapse Button on Left Border */
.collapse-button {
  position: absolute;
  top: 0.5rem;
  width: 20px;
  height: 20px;
  background: #191f27;
  border: 1px solid #282E35;
  border-radius: 3px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  z-index: 10;
  left: -.6rem;
}

.collapse-button:hover {
  background: #c084fc;
  border-color: #a855f7;
  transform: scale(1.1);
}

.collapse-button svg {
  width: 10px;
  height: 10px;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Load More Button */
.load-more-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  margin-top: 0.5rem;
  background: rgba(115, 87, 210, 0.1);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: 0.5rem;
  color: #7357D2;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-button:hover:not(:disabled) {
  background: rgba(115, 87, 210, 0.2);
  border-color: rgba(115, 87, 210, 0.5);
}

.load-more-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .comment-card {
    padding: 0.75rem;
  }

  .expand-link {
    font-size: 0.8rem;
  }

  .show-comments-link {
    padding: 0.5rem 0.75rem;
  }

  .collapse-button {
    width: 18px;
    height: 18px;
    left: -0.85rem;
  }

  .collapse-button svg {
    width: 9px;
    height: 9px;
  }

  /* Reduce indentation on mobile */
  .comment-item {
    padding-left: 0 !important;
  }

  .nested-comments {
    margin-left: 0.25rem;
  }
}

/* Deep nesting visual cue */
.comment-item[style*="60px"] .comment-card,
.comment-item[style*="80px"] .comment-card,
.comment-item[style*="100px"] .comment-card {
  background: rgba(30, 37, 47, 0.3);
}
</style>
